import { provide } from '@lit/context'
import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { css, html, nothing } from 'lit'
import { customElement, property, query, queryAssignedElements, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { fromResizeObserver } from '@schmancy/directives/layout'
import { distinctUntilChanged, filter, fromEvent, map, merge, takeUntil, tap, throttleTime } from 'rxjs'
import { SchmancyTabsKeepMountedContext, SchmancyTabsModeContext, TSchmancyTabsMode } from './context'
import SchmancyTab from './tab'
import '../typography/typography'

// The panels are slotted light-DOM children while the strip that controls them
// is rendered in this element's shadow root, and an IDREF only resolves inside
// its own tree scope — so `aria-controls="<panel id>"` written from the strip
// points at nothing (measured: axe `aria-valid-attr-value`). The association is
// made with `ariaControlsElements` instead, the element-reference form the
// platform added for exactly this, which reaches out of a shadow root into the
// host's tree. Each panel still gets an id for consumers to address it by.
let groupCount = 0

@customElement('schmancy-tab-group')
export default class SchmancyTabGroup extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		// The strip takes its own height out of the host's box and the panel row
		// takes the rest, so the two together never outgrow the box the group was
		// given. A host with no definite height sizes both rows to their content.
		// The column is minmax(0, 1fr) so a wide panel overflows inside its row
		// rather than widening the group past its container, as a block would.
		css`
			:host {
				display: grid;
				grid-template-rows: auto minmax(0, 1fr);
				grid-template-columns: minmax(0, 1fr);
				height: 100%;
			}
			:host([density='compact']) {
				container-type: inline-size;
			}
			:host([density='compact']) [role='tab'] {
				height: 2.5rem;
				padding-inline: 0.625rem;
				font-size: 0.75rem;
				line-height: 1rem;
			}
			@container (min-width: 36rem) {
				:host([density='compact']) [role='tab'] {
					height: 1.5rem;
					padding-inline: 0.375rem;
					font-size: 0.6875rem;
				}
			}
		`,
	]
	@provide({ context: SchmancyTabsModeContext })
	@property({ type: String })
	mode: TSchmancyTabsMode = 'tabs'

	/** Opt into the pill treatment — a filled stadium container with centred
	 *  labels. The default strip carries no container at all: labels sit on a
	 *  hairline baseline, left-aligned, the active one marked by a 2px
	 *  underline riding only its own width. */
	@property({ type: Boolean }) rounded = false

	/**
	 * `compact` is a dense strip for a tool: 24px tabs where the group itself is
	 * 36rem or wider, and 40px, a finger's, where it is narrower. It sizes against
	 * its own width, so it takes that width from what it sits in rather than from
	 * its tabs. A `rounded` strip ignores it.
	 */
	@property({ type: String, reflect: true }) density?: 'compact'

	/**
	 * Accessible name for the tab strip. A page carrying more than one strip
	 * needs each named for what it switches between; an `aria-label` on the
	 * host names it too, ahead of this.
	 */
	@property({ type: String }) label = 'Tabs'

	/**
	 * Keep every panel mounted and laid out, hiding the inactive ones instead of
	 * emptying them. Opt in when a panel holds a child whose state cannot be
	 * rebuilt from its properties — a document viewer's fit and scroll offset, a
	 * playing clip, a half-filled form — and the panels then overlap in one cell
	 * so the group's height stops depending on which one is showing.
	 *
	 * The cost is that every panel's content is live at once: leave this off for
	 * a group whose panels each pull their own data.
	 */
	@property({ type: Boolean, reflect: true, attribute: 'keep-mounted' })
	keepMounted = false

	/**
	 * What the panels actually consume. Scroll mode already renders every panel
	 * and lays them out one after another down the page, so keeping them mounted
	 * asks for nothing it does not already do and overlapping them would destroy
	 * the very geometry the scroll-spy reads.
	 */
	@provide({ context: SchmancyTabsKeepMountedContext })
	@state()
	private panelsKeptMounted = false

	@property({ type: String, reflect: true }) activeTab: string
	@queryAssignedElements({
		flatten: true,
	})
	private tabsElements!: Array<SchmancyTab>

	@query('#tabsNavigation') navElement!: HTMLElement
	@query('#tabsContent') tabsContent!: HTMLElement

	@state()
	private tabs: Array<SchmancyTab> = []

	readonly #groupId = `schmancy-tabs-${++groupCount}`

	connectedCallback(): void {
		super.connectedCallback()
		merge(
			fromEvent(window, 'scroll').pipe(
				throttleTime(1000),
				filter(() => this.mode === 'scroll'),
				map(() => {
					let closestDiv = null
					let closestDistance = Infinity
					this.tabsElements.forEach(div => {
						const distance =
							div.getBoundingClientRect().top - this.navElement.clientHeight + document.body.offsetHeight / 3

						if (distance < closestDistance && distance > 0) {
							closestDistance = distance
							closestDiv = div
						}
					})
					return closestDiv
				}),
				filter((el: SchmancyTab | null) => el !== null),
				tap((el: SchmancyTab) => {
					this.activeTab = el.value
				}),
			),
			// The strip is built from every tab's label and value, and a change to
			// either after the tab was slotted fires no slotchange.
			fromEvent(this, 'schmancy-tab-relabel').pipe(
				tap(e => {
					e.stopPropagation()
					this.hydrateTabs()
				}),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	firstUpdated() {
		// Scroll mode pads every panel by the sticky strip's height, known only once
		// the strip lays out. No other mode reads it, and a strip kept hidden never
		// gets a height to wait for.
		if (this.mode !== 'scroll') return
		fromResizeObserver(this.navElement)
			.pipe(
				map(() => this.navElement.clientHeight),
				filter(height => height > 0),
				distinctUntilChanged(),
				tap(height => {
					this.tabsElements.forEach(tab => {
						// A host is inline until told otherwise, and an inline box's padding moves nothing after it.
						tab.style.display = 'block'
						tab.style.paddingTop = `${height}px`
					})
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	hydrateTabs() {
		// The slotted <schmancy-tab> IS the panel the tab strip switches to, so
		// it is what carries the tabpanel role. Its name is the tab's own label
		// written out, not an `aria-labelledby` at the tab — that IDREF would
		// have to reach into this element's shadow root, which no reference of
		// either form is allowed to do. A consumer's own id is left alone.
		this.tabsElements.forEach((tab, index) => {
			if (!tab.id) tab.id = `${this.#groupId}-panel-${index}`
			tab.setAttribute('role', 'tabpanel')
			if (tab.label) tab.setAttribute('aria-label', tab.label)
			else tab.removeAttribute('aria-label')
		})
		const tabs = this.tabsElements
		this.tabs = tabs
		// The selection is the tab carrying `activeTab`. A tab renamed while selected
		// no longer carries it but is still the one marked active, so the selection
		// follows it. An `activeTab` naming a tab not slotted yet is the consumer's
		// choice and stays; only an empty one falls back to the first tab.
		this.activeTab =
			tabs.find(tab => tab.value === this.activeTab)?.value ??
			tabs.find(tab => tab.active)?.value ??
			(this.activeTab || tabs[0]?.value)
		tabs.forEach(tab => {
			tab.active = tab.value === this.activeTab
		})
		const lastTab = this.tabs?.[-1]
		if (lastTab) {
			lastTab.style.paddingBottom = lastTab.offsetHeight + 'px'
		}
	}

	tabChanged(selectedTab: { label: string; value: string }) {
		let activeTabElement: SchmancyTab | undefined
		this.tabsElements.forEach(tab => {
			if (tab.value === selectedTab.value) {
				tab.active = true
				activeTabElement = tab
				if (this.mode === 'scroll') {
					activeTabElement.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
						inline: 'start',
					})
				}
			} else {
				tab.active = false
			}
		})
		if (this.mode === 'tabs') {
			this.activeTab = selectedTab.value
		}
		this.dispatchEvent(new CustomEvent('tab-changed', { detail: this.activeTab }))
	}

	/**
	 * Arrow keys walk the strip and select as they go. Tab itself enters and
	 * leaves the strip — reaching the fifth tab must not cost four Tab presses
	 * on the way to the panel's own content.
	 */
	private moveTabFocus = (e: KeyboardEvent): void => {
		const buttons = Array.from(this.renderRoot.querySelectorAll<HTMLElement>('#tabsNavigation > [role="tab"]'))
		if (buttons.length === 0) return
		const focused = e.composedPath().find(node => node instanceof HTMLElement && buttons.includes(node)) as
			HTMLElement | undefined
		const current = focused ? buttons.indexOf(focused) : this.tabs.findIndex(tab => tab.value === this.activeTab)

		let next: number
		switch (e.key) {
			case 'ArrowRight':
				next = (current + 1) % buttons.length
				break
			case 'ArrowLeft':
				next = (current - 1 + buttons.length) % buttons.length
				break
			case 'Home':
				next = 0
				break
			case 'End':
				next = buttons.length - 1
				break
			default:
				return
		}
		e.preventDefault()
		const tab = this.tabs[next]
		if (tab) this.tabChanged({ label: tab.label, value: tab.value })
		buttons[next].focus()
	}

	protected willUpdate(changed: Map<PropertyKey, unknown>): void {
		super.willUpdate(changed)
		this.panelsKeptMounted = this.keepMounted && this.mode === 'tabs'
		// An `activeTab` the consumer sets switches the panel as well as the strip,
		// and keeps "marked active" meaning "selected": a hydrate that then finds
		// `activeTab` on no slotted tab reads a tab still marked active as a rename,
		// never as a stale choice.
		if (changed.has('activeTab')) {
			this.tabsElements.forEach(tab => {
				tab.active = tab.value === this.activeTab
			})
		}
	}

	protected render(): unknown {
		const roundedNavClasses = this.classMap({
			'bg-surface-default color-surface-on': true,
			'flex z-50 overflow-x-auto overflow-y-hidden': true,
			'sticky top-0 shadow-md': this.mode === 'scroll',
			'rounded-full': true,
		})

		const activeTab = {
			'text-primary-default': true,
		}

		const inactiveTab = {
			'border-transparent': true,
			'hover:text-surface-on': true,
			'hover:border-outlineVariant': true,
			'text-surface-onVariant': true,
		}

		// The strip scrolls sideways only, so nothing may hang past its padding box:
		// a scrolling strip turns that into vertical overflow whose scrollbar sits
		// over the last tab. The hairline is an inset shadow inside that box rather
		// than a border outside it, and the active underline covers it from inside.
		const compact = this.density === 'compact' && !this.rounded
		const hairline = 'inset-shadow-[0_-1px_0_0_var(--color-outline-variant)] inset-shadow-outline-variant/30'
		const underlineNavClasses = this.classMap({
			'flex items-center overflow-x-auto overflow-y-hidden': true,
			'gap-6': !compact,
			'min-w-0 [scrollbar-width:none]': compact,
			[hairline]: !compact,
			'sticky top-0 z-50 bg-surface-default shadow-md': this.mode === 'scroll',
		})

		return html`
			${when(
				this.rounded,
				() => html`
					<div
						id="tabsNavigation"
						role="tablist"
						class="${roundedNavClasses}"
						aria-label=${this.ariaLabel || this.label}
						@keydown=${this.moveTabFocus}
					>
						${repeat(
							this.tabs,
							tab => tab.value,
							tab => html`
								<button
									type="button"
									role="tab"
									aria-selected=${this.activeTab === tab.value}
									.ariaControlsElements=${[tab]}
									tabindex=${this.activeTab === tab.value ? 0 : -1}
									class=${this.classMap({
										// px-10/py-6 is the 24+16 / 12+12 this tab carried while it was a
										// <schmancy-button> wrapping a px-4 py-3 box, and xl is that host's
										// own rung — the geometry is the one every consumer already renders.
										'relative shrink-0 cursor-pointer rounded-xl px-10 py-6': true,
										'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default':
											true,
										...(this.activeTab === tab.value ? activeTab : inactiveTab),
									})}
									@click=${() => {
										this.tabChanged({
											label: tab.label,
											value: tab.value,
										})
									}}
								>
									<div
										class="absolute inset-0 z-0 rounded-xl hover:bg-primary-default hover:opacity-[var(--schmancy-sys-state-opacity-hover,0.08)] active:opacity-[var(--schmancy-sys-state-opacity-pressed,0.12)]"
									></div>
									<schmancy-typography class="h-full align-middle flex " type="title" token="md" weight="medium">
										${tab.label}
										${tab.failing > 0
											? html`<span data-failing class="ms-1 font-mono font-semibold tabular-nums text-error-default"
													><span aria-hidden="true">✕${tab.failing}</span><span class="sr-only">${tab.failingLabel || tab.failing}</span></span
												>`
											: nothing}
									</schmancy-typography>
									<div
										.hidden=${this.activeTab !== tab.value}
										class="border-primary-default absolute bottom-0 inset-x-6  border-solid border-2 rounded-t-full"
									></div>
								</button>
							`,
						)}
					</div>
				`,
				() => {
					const strip = html`
					<div
						id="tabsNavigation"
						role="tablist"
						class="${underlineNavClasses}"
						aria-label=${this.ariaLabel || this.label}
						@keydown=${this.moveTabFocus}
					>
						${repeat(
							this.tabs,
							tab => tab.value,
							tab => html`
								<button
									type="button"
									role="tab"
									aria-selected=${this.activeTab === tab.value}
									.ariaControlsElements=${[tab]}
									tabindex=${this.activeTab === tab.value ? 0 : -1}
									class=${compact
										? this.classMap({
												'relative flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap font-medium hover:bg-surface-container focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-default': true,
												'text-surface-on': this.activeTab === tab.value,
												'text-surface-onVariant': this.activeTab !== tab.value,
											})
										: 'relative min-h-11 min-w-11 shrink-0 cursor-pointer pb-2 pt-1'}
									@click=${() => this.tabChanged({ label: tab.label, value: tab.value })}
								>
									${compact
										? tab.label
										: html`<schmancy-typography
												type="label"
												token="lg"
												class=${this.activeTab === tab.value ? 'text-primary-default font-medium' : 'text-surface-onVariant'}
											>
												${tab.label}
											</schmancy-typography>`}
									${tab.failing > 0
										? html`<span data-failing class=${this.classMap({ 'font-mono font-semibold tabular-nums text-error-default': true, 'ms-1': !compact })}
												><span aria-hidden="true">✕${tab.failing}</span><span class="sr-only">${tab.failingLabel || tab.failing}</span></span
											>`
										: nothing}
									<span
										data-underline
										class="absolute inset-x-0 bottom-0 h-0.5 rounded-full transition-colors ${
											this.activeTab === tab.value ? 'bg-primary-default' : 'bg-transparent'
										}"
									></span>
								</button>
							`,
						)}
					</div>
`
					return compact
						? html`<div class=${this.classMap({ 'flex min-w-0 items-stretch': true, [hairline]: true })}>
								${strip}
								<div class="flex flex-1 items-center justify-end"><slot name="end"></slot></div>
							</div>`
						: strip
				},
			)}
			<section
				id="tabsContent"
				class=${this.classMap({
					// One cell every panel shares, so the panel the group is not
					// showing costs no height. A slot is `display: contents`, so the
					// panels themselves are this grid's items.
					grid: this.panelsKeptMounted,
				})}
			>
				<slot @slotchange=${() => this.hydrateTabs()}></slot>
			</section>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-tab-group': SchmancyTabGroup
	}
}
